import * as Joi from "joi";
import {
	Column,
	Entity,
	JoinColumn,
	JoinTable,
	ManyToMany,
	ManyToOne,
	OneToMany,
	OneToOne,
} from 'typeorm';
import { GenPartial } from 'typeormgen';
import { SetPieceESDoc } from './set-piece.interfaces';
import { DEPARTMENTS } from '../enums/enums';
import Episodic from '../episodics/episodic.entity';
import { Indexable } from '../indexable';
import Inventory from '../inventory/inventory.entity';
import SetMoneyTrackingPayment from '../set-money-tracking-payments/set-money-tracking-payment.entity';
import SetPiecePicture from '../set-piece-pictures/set-piece-picture.entity';
import SetPieceSet from '../set-piece-sets/set-piece-set.entity';
import SetPieceStatus from '../set-piece-statuses/set-piece-status.entity';
import SetPieceType from '../set-piece-types/set-piece-type.entity';
import SetStorageLocation from '../set-storage-locations/set-storage-location.entity';
import SetWrapBoxEpisodicSetPiece from '../set-wrap-box-set-pieces/set-wrap-box-set-piece.entity';
import SetWrapBox from '../set-wrap-boxes/set-wrap-box.entity';
import Set from '../sets/set.entity';
import Vendor from '../vendors/vendor.entity';

@Entity('set_pieces')
export default class SetPiece
	extends Inventory
	implements Indexable<SetPieceESDoc> {
	static tableName = 'set_pieces';

	static deptId = DEPARTMENTS.SET;

	static columns = [
		...Inventory.columns,
		'dimensions',
		'series_set_piece_id',
		'set_piece_wrap_box_id',
		'set_ids',
	];

	@Column('int')
	series_set_piece_id: number;

	@Column('varchar')
	dimensions: string;

	@Column('int')
	set_piece_wrap_box_id: number;

	@OneToMany(
		(type) => SetPiecePicture,
		(setPiecePictures) => setPiecePictures.setPiece
	)
	setPiecePictures: SetPiecePicture[];

	@ManyToOne((type) => Episodic, (episodic) => episodic.setPieces)
	@JoinColumn({
		name: 'episodic_id',
	})
	episodic: Episodic;

	@ManyToOne((type) => SetPieceStatus, (status) => status.setPieces)
	@JoinColumn({
		name: 'status_id',
	})
	status: SetPieceStatus;

	@ManyToOne((type) => Vendor, (vendor) => vendor.setPieces)
	@JoinColumn({
		name: 'vendor_id',
	})
	vendor: Vendor;

	@ManyToOne((type) => SetPieceType, (category) => category.setPieces)
	@JoinColumn({
		name: 'category_id',
	})
	category: SetPieceType;

	@ManyToOne(
		(type) => SetStorageLocation,
		(setStorageLocation) => setStorageLocation.setPieces
	)
	@JoinColumn({
		name: 'storage_location_id',
	})
	storageLocation: SetStorageLocation;

	@OneToMany((type) => SetPieceSet, (setPieceSet) => setPieceSet.setPiece)
	setPieceSets: SetPieceSet[];

	@ManyToMany(() => Set, (set) => set.setPieces)
	@JoinTable({
		name: 'set_piece_sets',
		joinColumn: { name: 'set_piece_id' },
		inverseJoinColumn: { name: 'set_id' },
	})
	sets: Set[];

	@OneToOne(
		(type) => SetWrapBoxEpisodicSetPiece,
		(setWrapBoxEpisodicSetPiece) => setWrapBoxEpisodicSetPiece.setPiece
	)
	setWrapBoxEpisodicSetPiece: SetWrapBoxEpisodicSetPiece;

	@ManyToMany(() => SetWrapBox, (setWrapBox) => setWrapBox.setPieces)
	@JoinTable({
		name: 'set_wrap_box_set_pieces',
		joinColumn: { name: 'set_piece_id' },
		inverseJoinColumn: { name: 'set_piece_wrap_box_id' },
	})
	setWrapBoxes: SetWrapBox[];

	@ManyToMany(
		() => SetMoneyTrackingPayment,
		(setMoneyTrackingPayment) => setMoneyTrackingPayment.setPieces
	)
	@JoinTable({
		name: 'set_piece_payments',
		joinColumn: { name: 'set_piece_id' },
		inverseJoinColumn: { name: 'set_money_tracking_payment_id' },
	})
	setMoneyTrackingPayments: SetMoneyTrackingPayment[];

	readonly department = SetPiece.deptId;

	static rules() {
		return {
			...Inventory.rules(),
			mobile_id: Joi.string().max(36).allow(null, ''),
			series_set_piece_id: Joi.number().integer().allow(null),
			dimensions: Joi.string().max(50).allow(null, ''),
			setPieceSets: Joi.array().items(SetPieceSet.rules()).allow(null),
			set_ids: Joi.array().items(Joi.number().integer()),
			set_piece_wrap_box_id: Joi.number().integer().allow(null),
			setPiecePictures: Joi.array().items(SetPiecePicture.rules()).allow(null),
		};
	}

	toJSON() {
		return {
			...super.toJSON(),
			series_set_piece_id: this.series_set_piece_id,
			dimensions: this.dimensions,
			set_piece_wrap_box_id: this.set_piece_wrap_box_id,
			department: this.department,
			setPieceSets: this.setPieceSets ? this.setPieceSets : [],
			setPiecePictures: this.setPiecePictures ? this.setPiecePictures : [],
			main_photo_id: this.main_photo_id
				? this.main_photo_id
				: this.setPiecePictures && this.setPiecePictures.length > 0
				? this.setPiecePictures.find((pic) => pic.main_photo === true)
					? this.setPiecePictures.find((pic) => pic.main_photo === true)
							.picture_id
					: undefined
				: undefined,
			setWrapBoxes: this.setWrapBoxes,
			wrap_box_id: this.set_piece_wrap_box_id,
			inv_wrap_box_names: this.inv_wrap_box_names
				? this.inv_wrap_box_names
				: this.setWrapBoxes && this.setWrapBoxes.length > 0
				? this.getWrapBoxNames(this.setWrapBoxes)
				: undefined,
			storageLocation: this.storageLocation,
			storage_location_name: this.storage_location_name
				? this.storage_location_name
				: this.storageLocation
				? this.storageLocation.name
				: undefined,
			category: this.category,
			category_name: this.category_name
				? this.category_name
				: this.category
				? this.category.name
				: undefined,
			status: this.status,
			status_name: this.status_name
				? this.status_name
				: this.status
				? this.status.name
				: undefined,
			vendor: this.vendor,
			vendor_name: this.vendor_name
				? this.vendor_name
				: this.vendor
				? this.vendor.name
				: undefined,
		};
	}

	toESDoc(data?: { storageLocation: SetStorageLocation }) {
		let storage_location_name: string;

		if (data) {
			const { storageLocation } = data;

			if (storageLocation === null) {
				storage_location_name = null;
			} else if (storageLocation) {
				storage_location_name = storageLocation.name;
			}
		}

		return {
			id: this.id,
			episodic_id: this.episodic_id,
			name: this.name,
			brand: this.brand,
			description: this.description,
			asset_num: this.asset_num,
			disposition_notes: this.disposition_notes,
			size: this.size,
			storage_location_id: this.storage_location_id,
			storage_location_name,
		};
	}

	constructor(props?: GenPartial<SetPiece>) {
		super(props);
	}
}
